// Shared move-history model + navigation, used by both the live game screen
// (browse/replay a game in place) and the analysis board (plan §9). Given the
// moves (UCI is authoritative; SAN is recomputed so it's always consistent with
// the position), produce node 0 = start and node i = the position after ply i,
// each with a normalised FEN. Also derives the captured-piece "material legend"
// for any position, and the navigation controls/keymap the two screens share.
// Pure (chessops only, no DOM) except the small DOM helpers at the bottom.

import { Chess } from "chessops/chess";
import { INITIAL_FEN, makeFen, parseFen } from "chessops/fen";
import { makeSanAndPlay } from "chessops/san";
import { parseUci } from "chessops/util";
import type { Color } from "./types.ts";
import { t } from "../i18n/index.ts";

export interface GameNode {
  index: number; // 0 = start position
  fen: string; // normalised FEN of this position
  turn: Color; // side to move here
  /** The move that led into this node (absent for node 0). */
  uci?: string;
  san?: string;
  moveColor?: Color; // colour that played `uci`
  /** Annotation attached to the move (imported PGN `{comment}`, plan B1). */
  comment?: string;
}

export interface Review {
  initialFen: string;
  nodes: GameNode[]; // length = plies + 1
}

/**
 * Replay `moves` from `initialFen` (default: standard start), producing one node
 * per position. Stops early (returning what it has) if a move doesn't parse or
 * is illegal — a defensive guard; real game records replay cleanly.
 */
export function buildReview(
  moves: { uci: string; comment?: string }[],
  initialFen: string = INITIAL_FEN,
): Review {
  const setup = parseFen(initialFen).unwrap(
    (s) => s,
    () => parseFen(INITIAL_FEN).unwrap(),
  );
  const pos = Chess.fromSetup(setup).unwrap(
    (p) => p,
    () => Chess.default(),
  );

  const startFen = makeFen(pos.toSetup());
  const nodes: GameNode[] = [{ index: 0, fen: startFen, turn: pos.turn as Color }];

  for (let i = 0; i < moves.length; i++) {
    const move = parseUci(moves[i].uci);
    if (!move || !pos.isLegal(move)) break;
    const mover = pos.turn as Color;
    const san = makeSanAndPlay(pos, move); // mutates pos + returns SAN
    nodes.push({
      index: i + 1,
      fen: makeFen(pos.toSetup()),
      turn: pos.turn as Color,
      uci: moves[i].uci,
      san,
      moveColor: mover,
      ...(moves[i].comment ? { comment: moves[i].comment } : {}),
    });
  }

  return { initialFen: startFen, nodes };
}

/** The node reached by playing `uci` from `parent`, or null if illegal. Both
 *  castling encodings (e1g1 and e1h1) are accepted — chessops normalises them
 *  to the same castle, so callers can compare candidates by resulting FEN. */
export function childNode(parent: GameNode, uci: string): GameNode | null {
  const pos = positionOf(parent.fen);
  if (!pos) return null;
  const move = parseUci(uci);
  if (!move || !pos.isLegal(move)) return null;
  const mover = pos.turn as Color;
  const san = makeSanAndPlay(pos, move);
  return {
    index: parent.index + 1,
    fen: makeFen(pos.toSetup()),
    turn: pos.turn as Color,
    uci,
    san,
    moveColor: mover,
  };
}

/** Replay a UCI principal variation from `fen` into human SAN with move numbers
 *  ("3.Bb5 a6 4.Ba4 …"). Stops at the first illegal move; appends "…" when the
 *  pv was longer than `maxPlies`. */
export function pvToSan(fen: string, pv: string[], maxPlies = 8): string {
  const pos = positionOf(fen);
  if (!pos) return "";
  const parts: string[] = [];
  const shown = Math.min(pv.length, maxPlies);
  for (let i = 0; i < shown; i++) {
    const move = parseUci(pv[i]);
    if (!move || !pos.isLegal(move)) break;
    const white = pos.turn === "white";
    const num = pos.fullmoves;
    const san = makeSanAndPlay(pos, move);
    if (white) parts.push(`${num}.${san}`);
    else if (i === 0) parts.push(`${num}…${san}`);
    else parts.push(san);
  }
  if (parts.length === shown && pv.length > maxPlies) parts.push("…");
  return parts.join(" ");
}

function positionOf(fen: string): Chess | undefined {
  const setup = parseFen(fen).unwrap(
    (s) => s,
    () => undefined,
  );
  if (!setup) return undefined;
  return Chess.fromSetup(setup).unwrap(
    (p) => p,
    () => undefined,
  );
}

// ---------------------------------------------------------------------------
// Captured-piece legend (ask: "palette of taken pieces")
// ---------------------------------------------------------------------------

export type Role = "pawn" | "knight" | "bishop" | "rook" | "queen";

/** Pieces each side has captured (their opponent's, removed from the board),
 *  plus the running material balance (positive = White ahead, by points). */
export interface Captured {
  white: Role[]; // black pieces White has captured, high value first
  black: Role[]; // white pieces Black has captured, high value first
  diff: number; // White material minus Black material, in points
}

const START_COUNT: Record<Role, number> = { queen: 1, rook: 2, bishop: 2, knight: 2, pawn: 8 };
const VALUE: Record<Role, number> = { queen: 9, rook: 5, bishop: 3, knight: 3, pawn: 1 };
const LETTER_TO_ROLE: Record<string, Role> = { q: "queen", r: "rook", b: "bishop", n: "knight", p: "pawn" };
const ORDER: Role[] = ["queen", "rook", "bishop", "knight", "pawn"];

/**
 * Captured material for a FEN, derived from what's missing off the board versus
 * the standard start (start-minus-board — no per-move capture log needed). A
 * promotion is a rare edge that skews this slightly; acceptable for a legend.
 */
export function capturedFor(fen: string): Captured {
  const placement = fen.split(" ")[0] ?? "";
  const w: Record<Role, number> = { queen: 0, rook: 0, bishop: 0, knight: 0, pawn: 0 };
  const b: Record<Role, number> = { queen: 0, rook: 0, bishop: 0, knight: 0, pawn: 0 };
  for (const ch of placement) {
    const role = LETTER_TO_ROLE[ch.toLowerCase()];
    if (!role) continue; // digits, '/', kings
    if (ch === ch.toUpperCase()) w[role]++;
    else b[role]++;
  }

  const white: Role[] = [];
  const black: Role[] = [];
  let diff = 0;
  for (const role of ORDER) {
    const blackGone = Math.max(0, START_COUNT[role] - b[role]); // captured by White
    const whiteGone = Math.max(0, START_COUNT[role] - w[role]); // captured by Black
    for (let i = 0; i < blackGone; i++) white.push(role);
    for (let i = 0; i < whiteGone; i++) black.push(role);
    diff += (w[role] - b[role]) * VALUE[role];
  }
  return { white, black, diff };
}

const GLYPH: Record<Color, Record<Role, string>> = {
  white: { queen: "♕", rook: "♖", bishop: "♗", knight: "♘", pawn: "♙" },
  black: { queen: "♛", rook: "♜", bishop: "♝", knight: "♞", pawn: "♟" },
};

/**
 * Render the captured legend for one side into `el`: the pieces `color` captured
 * (i.e. the opponent's), grouped by type with a small `×N` count, then a `+N`
 * advantage pill when this side is ahead. `cap` comes from {@link capturedFor}.
 */
export function paintCaptured(el: HTMLElement, color: Color, cap: Captured): void {
  const taken = color === "white" ? cap.white : cap.black; // opponent's colour glyphs
  const oppColor: Color = color === "white" ? "black" : "white";
  const advantage = color === "white" ? cap.diff : -cap.diff;

  el.replaceChildren();
  if (taken.length === 0 && advantage <= 0) return;

  // Group by role — `taken` is already high-value-first, so Map insertion order
  // gives us queen→pawn grouping without re-sorting.
  const counts = new Map<Role, number>();
  for (const role of taken) counts.set(role, (counts.get(role) ?? 0) + 1);

  const pieces = document.createElement("span");
  pieces.className = `cap-pieces cap-${oppColor}`;
  for (const [role, n] of counts) {
    const group = document.createElement("span");
    group.className = "cap-group";
    const glyph = document.createElement("span");
    glyph.className = "cap-glyph";
    glyph.textContent = GLYPH[oppColor][role];
    group.append(glyph);
    if (n > 1) {
      const count = document.createElement("span");
      count.className = "cap-count";
      count.textContent = `×${n}`;
      group.append(count);
    }
    pieces.append(group);
  }
  el.append(pieces);

  if (advantage > 0) {
    const adv = document.createElement("span");
    adv.className = "cap-adv";
    adv.textContent = `+${advantage}`;
    el.append(adv);
  }
}

// ---------------------------------------------------------------------------
// Navigation controls + keymap (shared chrome for both boards)
// ---------------------------------------------------------------------------

export type NavAction = "first" | "prev" | "next" | "last";

/** Flat, monochrome transport icons drawn as inline SVG (currentColor). We can't
 *  use the natural glyphs ⏮/⏭ (U+23EE/U+23ED): those media-control symbols default
 *  to *emoji* presentation on iOS, rendering as out-of-place colour boxes, and the
 *  text variation selector (U+FE0E) is unreliable on Safari. Drawing them keeps
 *  the whole row crisp and consistent with the ◀ ▶ step arrows on every platform. */
const NAV_ICON = {
  first: `<rect x="5" y="5.5" width="2.6" height="13" rx="0.6"/><path d="M20 5.5 20 18.5 9 12Z"/>`,
  prev: `<path d="M17 5 17 19 7 12Z"/>`,
  next: `<path d="M7 5 7 19 17 12Z"/>`,
  last: `<path d="M4 5.5 4 18.5 15 12Z"/><rect x="16.4" y="5.5" width="2.6" height="13" rx="0.6"/>`,
} as const;

function navIcon(name: keyof typeof NAV_ICON): string {
  return `<svg viewBox="0 0 24 24" width="1.15em" height="1.15em" fill="currentColor" aria-hidden="true">${NAV_ICON[name]}</svg>`;
}

/** The |◀ ◀ ▶ ▶| (+ optional ⇅ flip) button row. Buttons carry `data-nav`. */
export function navControls(opts: { flip?: boolean } = {}): HTMLElement {
  const nav = document.createElement("div");
  nav.className = "nav";
  nav.innerHTML =
    `<button class="btn ghost" data-nav="first" title="${t("review.startT")}" aria-label="${t("review.start")}" type="button">${navIcon("first")}</button>` +
    `<button class="btn ghost" data-nav="prev" title="${t("review.prevT")}" aria-label="${t("review.prev")}" type="button">${navIcon("prev")}</button>` +
    `<button class="btn ghost" data-nav="next" title="${t("review.nextT")}" aria-label="${t("review.next")}" type="button">${navIcon("next")}</button>` +
    `<button class="btn ghost" data-nav="last" title="${t("review.endT")}" aria-label="${t("review.end")}" type="button">${navIcon("last")}</button>` +
    (opts.flip ? `<button class="btn ghost" data-nav="flip" title="${t("review.flip")}" aria-label="${t("review.flip")}" type="button">⇅</button>` : "");
  return nav;
}

/** Map a keydown to a nav action (←/→ step, ↑/Home start, ↓/End end), or null. */
export function navKey(e: KeyboardEvent): NavAction | null {
  switch (e.key) {
    case "ArrowLeft":
      return "prev";
    case "ArrowRight":
      return "next";
    case "ArrowUp":
    case "Home":
      return "first";
    case "ArrowDown":
    case "End":
      return "last";
    default:
      return null;
  }
}

/** Scroll `el` into view *within* `list` only. `scrollIntoView` scrolls every
 *  scrollable ancestor — including the page — so on mobile (move list stacked
 *  below the board) it yanked the viewport down on every move. */
export function scrollIntoListView(list: HTMLElement, el: HTMLElement): void {
  const listRect = list.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  if (elRect.top < listRect.top) {
    list.scrollTop += elRect.top - listRect.top;
  } else if (elRect.bottom > listRect.bottom) {
    list.scrollTop += elRect.bottom - listRect.bottom;
  }
}

/** True when a keydown should be left alone (user is typing, e.g. chat). */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}
